import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Map } from 'immutable';
import useNearView from '../useNearView';
import CardFace from '../Card/CardFace';
import CardZoom, { ImageZoom, useCardSteps } from '../CardZoom';
import { Muted, PlainButton } from '../Controls';
import { Panel } from '../Page';
import { deckBoxUrl } from '../services/helpers';
import { ENERGY, TRAINER } from '../constants';
import { EnergySymbol } from '../Energy';
import { Box, CardCount, CardGrid, CardSlot, DeckHead, Group } from './styled';
import { releaseDate } from './releaseDate';

/** A deck's cards in the order a deck list prints them: Pokémon, then Trainers, then Energy. */
const KINDS = [['Pokémon', card => !isKind(card, TRAINER) && !isKind(card, ENERGY)], ['Trainers', card => isKind(card, TRAINER)], ['Energy', card => isKind(card, ENERGY)]];
const isKind = (card, kind) => card.get('cardTypes').some(type => type.get('id') === kind);

/** About how tall a deck's cards are, held as room until they're drawn, so the page doesn't jump as you scroll. */
const CARDS_ROOM_PX = 640;

/**
 * One deck: its box and name always; its cards (by kind, with counts) once you scroll near it, so a page of 25 decks
 * doesn't load hundreds of pictures at once.
 */
export const DeckPanel = ({ deck, cardsById, onCard, onBox }) => {
  const [ref, near] = useNearView();
  const cards = deck.get('cards').map(entry => ({ card: cardsById.get(entry.get('card_id')), count: entry.get('count') }))
    .filter(entry => entry.card).toArray();
  // Its box, front then back, as far as there are pictures of it.
  const pictures = [['image', 'box'], ['image_back', 'back of the box']].filter(([key]) => deck.get(key))
    .map(([key, what]) => ({ key, src: deckBoxUrl(deck, key, { full: true }), caption: `${deck.get('name')}, ${what}` }));
  // In the order they're shown: Pokémon, Trainers, Energy.
  const shownInOrder = KINDS.flatMap(([, test]) => cards.filter(entry => test(entry.card)).map(entry => entry.card));

  return (
    <Panel ref={ref} style={deck.get('playable') === false ? { opacity: 0.6 } : undefined}>
      <DeckHead>
        {pictures.map(({ key, caption }, index) => (
          <PlainButton
            key={key}
            onClick={() => onBox({ pictures, index })}
            aria-label={caption}
          >
            <Box src={deckBoxUrl(deck, key)} alt="" />
          </PlainButton>
        ))}
        <div>
          <h2>{deck.get('name')}</h2>
          <Muted>
            {cards.reduce((sum, entry) => sum + entry.count, 0)} cards
            {deck.get('released_on') && ` · released ${releaseDate(deck.get('released_on'))}`}
          </Muted>
          <div aria-label="Energy" style={{ display: 'flex', gap: 3, marginTop: 4 }}>
            {deckEnergyTypes(deck, cardsById).map(type => <EnergySymbol key={type} type={type} size="18px" />)}
          </div>
          {deck.get('playable') === false && <Muted as="div">Not playable yet: some of its cards aren't ready.</Muted>}
        </div>
      </DeckHead>
      {!near && <div style={{ height: CARDS_ROOM_PX }} />}
      {near && KINDS.map(([title, test]) => {
        const group = cards.filter(entry => test(entry.card));
        return group.length > 0 && (
          <Group key={title}>
            <h3>{title} · {group.reduce((sum, entry) => sum + entry.count, 0)}</h3>
            <CardGrid>
              {group.map(({ card, count }) => (
                <CardSlot key={card.get('id')}>
                  <PlainButton onClick={() => onCard(shownInOrder, shownInOrder.indexOf(card))} aria-label={`${count} × ${card.get('name')}`}>
                    <CardFace card={card} />
                  </PlainButton>
                  <CardCount>×{count}</CardCount>
                </CardSlot>
              ))}
            </CardGrid>
          </Group>
        );
      })}
    </Panel>
  );
};

DeckPanel.propTypes = {
  deck: PropTypes.instanceOf(Map).isRequired,
  cardsById: PropTypes.instanceOf(Map).isRequired,
  onCard: PropTypes.func.isRequired,
  onBox: PropTypes.func.isRequired,
};


/**
 * The energy types a deck's Energy cards provide, most first ("fire", "colorless"), for its symbols.
 *
 * @param {Map} deck with its cards ({ card_id, count })
 * @param {Map} cardsById the catalog
 * @returns {string[]}
 */
export function deckEnergyTypes(deck, cardsById) {
  const counts = {};
  deck.get('cards').forEach((entry) => {
    const card = cardsById.get(entry.get('card_id'));
    if (card && isKind(card, ENERGY) && card.get('energy_id')) {
      counts[card.get('energy_id')] = (counts[card.get('energy_id')] || 0) + entry.get('count');
    }
  });
  return Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
}

/**
 * Looking closer at a deck (DeckPanel): its cards full size, stepping through them in the order shown, and its box,
 * front and back. Returns the panel's onCard and onBox, the views to draw, and whether one is open.
 */
export function useDeckZooms() {
  const [deckCards, setDeckCards] = useState([]);
  const steps = useCardSteps(deckCards);
  const onCard = (list, i) => {
    setDeckCards(list);
    steps.open(i);
  };
  // A deck's box pictures full size, front then back: { pictures: [{ src, caption }], index }.
  const [box, setBox] = useState(null);
  const boxShown = box && box.pictures[box.index];

  const zooms = (
    <>
      {steps.zoom && <CardZoom {...steps.zoom} />}
      {boxShown && (
        <ImageZoom
          src={boxShown.src}
          caption={boxShown.caption}
          onClose={() => setBox(null)}
          onPrev={box.pictures.length > 1 ? (box.index > 0 ? () => setBox({ ...box, index: box.index - 1 }) : null) : undefined}
          onNext={box.pictures.length > 1 ? (box.index < box.pictures.length - 1 ? () => setBox({ ...box, index: box.index + 1 }) : null) : undefined}
        />
      )}
    </>
  );

  return { onCard, onBox: setBox, zooms, open: !!steps.zoom || !!boxShown };
}
